import React, { useEffect, useRef, useState } from 'react'
import { replaceLink } from 'shared/lib'
import { ShortenLinkTooltip } from 'entities/ShortLink/ui/ShortenLinkTooltip/ShortenLinkTooltip'
import { useEntitiesContext } from 'entities/EntitiesExport'

export function useShortenLink({
  trigger,
  updateTriggerDeps,
  setUpdateTriggerContent,
  addContent,
  isMarketing,
}: {
  updateTriggerDeps: Array<HTMLElement | HTMLDivElement | string | null>
  trigger: HTMLDivElement | null
  setUpdateTriggerContent: () => void
  addContent: (value: string) => void
  isMarketing?: boolean
}) {
  const entities = useEntitiesContext()
  const { shortLinkStore } = entities
  const refVisibleTooltip = useRef(false)
  const [currentLink, setCurrentLink] = useState<HTMLAnchorElement | undefined>(undefined)

  const onHoverLink = (link: HTMLAnchorElement) => () => {
    if (shortLinkStore.canShortUrl(link.href)) {
      refVisibleTooltip.current = true
      setCurrentLink(link)
    }
  }

  const onLeaveLink = () => {
    refVisibleTooltip.current = false
    setTimeout(() => {
      if (!refVisibleTooltip.current) {
        setCurrentLink(undefined)
      }
    }, 1000)
  }

  useEffect(() => {
    if (!trigger) return

    const listeners: Array<{ el: HTMLAnchorElement; event: string; handler: EventListener }> = []

    const timeoutId = setTimeout(() => {
      const links: NodeListOf<HTMLAnchorElement> = trigger.querySelectorAll('a')
      links.forEach((link) => {
        if (shortLinkStore.canShortUrl(link.href)) {
          const onEnter = onHoverLink(link)
          link.addEventListener('mouseenter', onEnter)
          link.addEventListener('mouseleave', onLeaveLink)
          listeners.push({ el: link, event: 'mouseenter', handler: onEnter })
          listeners.push({ el: link, event: 'mouseleave', handler: onLeaveLink })
        }
      })
    })

    return () => {
      clearTimeout(timeoutId)
      listeners.forEach(({ el, event, handler }) => el.removeEventListener(event, handler))
    }
  }, updateTriggerDeps)

  const onHideLinkTooltips = () => {
    setCurrentLink(undefined)
  }

  const onSuccess = () => {
    setUpdateTriggerContent()
    setCurrentLink(undefined)
  }

  return {
    onHideLinkTooltips,
    linkTooltipComponent: (
      <>
        <ShortenLinkTooltip
          trigger={currentLink}
          isMarketing={isMarketing}
          onLeaveTooltip={onLeaveLink}
          refVisibleTooltip={refVisibleTooltip}
          onSuccess={onSuccess}
          onAddShortLink={(link: string) => addContent && addContent(replaceLink(link) + '&nbsp;')}
          onHideLinkTooltips={onHideLinkTooltips}
        />
      </>
    ),
  }
}
